/**
 * BuyVaultAccessModal — Feb 25 2026 (Upgrade Vault).
 *
 * One-time £20 Vault Access purchase with two routes:
 *   1) PayPal (any region)              → opens PayPal approve URL
 *   2) Nigeria bank transfer (Moniepoint) → shows account details, user
 *      taps "I've Transferred · Notify Admin", admin reviews & approves.
 */
import React, { useEffect, useState } from 'react';
import { X, CreditCard, Building2, Copy, Check, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';
import GatewayPicker from './GatewayPicker';

const BuyVaultAccessModal = ({ onClose, onPaid }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // step: 'choose' | 'bank' | 'awaiting' | 'paypal-loading' | 'stripe-loading'
  const [step, setStep] = useState('choose');
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [bankResult, setBankResult] = useState(null); // { reference, ... }
  const [copied, setCopied] = useState('');
  const [gateway, setGateway] = useState(null);

  useEffect(() => {
    if (PAYMENTS_DISABLED) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/vault/buy/info`);
        if (r.ok && !cancelled) setInfo(await r.json());
      } catch {
        if (!cancelled) setError('Could not load purchase info.');
      }
    })();
    return () => { cancelled = true; };
  }, [BACKEND_URL]);

  const startStripe = async () => {
    setBusy(true);
    setError('');
    setStep('stripe-loading');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/payments/vault/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          provider: gateway?.provider,
          is_diaspora_card: !!gateway?.is_diaspora_card,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) {
        setError(data.detail || 'Could not start checkout. Please try again.');
        setStep('choose');
        return;
      }
      window.location.href = data.checkout_url;
    } catch {
      setError('Network error. Please try again.');
      setStep('choose');
    } finally {
      setBusy(false);
    }
  };

  const startPaypal = async () => {
    setBusy(true);
    setError('');
    setStep('paypal-loading');
    try {
      const returnUrl = `${window.location.origin}/payment/callback?purpose=vault`;
      const cancelUrl = `${window.location.origin}/vault`;
      const res = await authFetch(`${BACKEND_URL}/api/vault/buy/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: returnUrl, cancel_url: cancelUrl }),
      });
      const data = await res.json();
      if (!res.ok || !data.approve_url) {
        setError(data.detail || 'Could not start PayPal. Please try again.');
        setStep('choose');
        return;
      }
      window.location.href = data.approve_url;
    } catch {
      setError('Network error. Please try again.');
      setStep('choose');
    } finally {
      setBusy(false);
    }
  };

  const submitBankTransfer = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/vault/buy/bank-transfer`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Could not submit. Please try again.');
        return;
      }
      setBankResult(data);
      setStep('awaiting');
      onPaid?.();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const copyText = (label, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    });
  };

  const Header = ({ title, subtitle }) => (
    <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-700">
      <div className="min-w-0">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <button
        onClick={onClose}
        className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-slate-800"
        aria-label="Close"
        data-testid="buy-vault-close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const price = info?.price_gbp ?? 20;

  if (PAYMENTS_DISABLED) {
    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <div
          className="w-full max-w-md rounded-2xl border border-amber-300/35 bg-slate-900 p-6 text-center shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-label="Vault payments unavailable"
          data-testid="buy-vault-payments-disabled"
        >
          <ShieldCheck className="mx-auto h-10 w-10 text-amber-300" />
          <h3 className="mt-3 text-lg font-bold text-white">Vault checkout unavailable</h3>
          <p className="mt-2 text-sm text-amber-100">{PAYMENTS_UNAVAILABLE_MESSAGE}</p>
          <Button onClick={onClose} className="mt-5 w-full bg-slate-700 hover:bg-slate-600 text-white">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div
        className="w-full max-w-md bg-slate-900 border border-fuchsia-500/40 rounded-2xl shadow-[0_0_60px_-15px_rgba(217,70,239,0.45)] overflow-hidden"
        data-testid="buy-vault-modal"
      >
        {step === 'choose' && (
          <>
            <Header
              title="Buy Vault Access"
              subtitle={`One-time payment · £${price}`}
            />
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-300 leading-relaxed">
                Unlocking the vault is a one-time £{price} purchase. Pick your
                preferred payment route below — you'll get a private code from
                an admin once payment is confirmed.
              </p>

              {/* Region-aware gateway picker — flips the "Pay" button below
                  to use the chosen provider's currency + rails. */}
              <GatewayPicker onChange={setGateway} baseGbp={Number(price) || undefined} compact />

              <button
                onClick={startStripe}
                disabled={busy}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-gradient-to-r from-fuchsia-600/30 to-indigo-700/30 border border-fuchsia-400/40 hover:from-fuchsia-600/50 hover:to-indigo-700/50 transition-all disabled:opacity-50"
                data-testid="buy-vault-stripe"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-fuchsia-500/30 border border-fuchsia-300/40 flex items-center justify-center shrink-0">
                    <CreditCard className="w-5 h-5 text-fuchsia-100" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Pay with {gateway?.provider ? gateway.provider.charAt(0).toUpperCase() + gateway.provider.slice(1) : 'card'}
                    </p>
                    <p className="text-[11px] text-fuchsia-200/80">
                      {gateway?.provider === 'paypal'
                        ? 'PayPal balance or card'
                        : gateway?.provider === 'paystack'
                          ? 'Card · USSD · Bank · Mobile money'
                          : gateway?.provider === 'flutterwave'
                            ? 'Card · M-Pesa · USSD · Bank transfer'
                            : 'Card · Apple Pay · Google Pay · Link'}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-bold text-fuchsia-100">£{price}</span>
              </button>

              {info?.paypal_enabled && (
                <button
                  onClick={startPaypal}
                  disabled={busy}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-all disabled:opacity-50"
                  data-testid="buy-vault-paypal"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-300/30 flex items-center justify-center shrink-0">
                      <CreditCard className="w-4 h-4 text-blue-200" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs font-semibold text-gray-200">
                        Or pay with PayPal
                      </p>
                      <p className="text-[10px] text-gray-500">
                        Backup option · PayPal balance or card
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">£{price}</span>
                </button>
              )}

              <button
                onClick={() => setStep('bank')}
                disabled={busy}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-gradient-to-r from-emerald-700/30 to-green-800/30 border border-emerald-500/40 hover:from-emerald-700/50 hover:to-green-800/50 transition-all"
                data-testid="buy-vault-bank-transfer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/30 border border-emerald-300/40 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-emerald-200" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-sm font-semibold text-white">
                      🇳🇬 Nigeria — Bank transfer
                    </p>
                    <p className="text-[11px] text-emerald-200/80">
                      Moniepoint · approved by admin
                    </p>
                  </div>
                </div>
                <span className="text-sm font-bold text-emerald-200">£{price}</span>
              </button>

              {error && <p className="text-xs text-red-400">{error}</p>}
            </div>
          </>
        )}

        {step === 'paypal-loading' && (
          <>
            <Header title="Redirecting to PayPal…" />
            <div className="p-8 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-sm text-gray-400">Opening secure PayPal checkout…</p>
            </div>
          </>
        )}

        {step === 'stripe-loading' && (
          <>
            <Header title="Redirecting to Stripe…" />
            <div className="p-8 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
              <p className="text-sm text-gray-400">Opening secure Stripe checkout…</p>
            </div>
          </>
        )}

        {step === 'bank' && (
          <>
            <Header
              title="Bank transfer · Nigeria"
              subtitle={`Send £${price} (or its NGN equivalent) to:`}
            />
            <div className="p-5 space-y-3">
              {info?.bank ? (
                <div className="bg-slate-800/70 border border-emerald-500/30 rounded-xl p-4 space-y-2.5">
                  {[
                    ['Bank', info.bank.name],
                    ['Account', info.bank.account],
                    ['Account name', info.bank.holder],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">
                          {label}
                        </p>
                        <p className="text-sm font-mono text-white truncate" data-testid={`bank-${label.toLowerCase().replace(' ','-')}`}>
                          {value || '—'}
                        </p>
                      </div>
                      {value && (
                        <button
                          onClick={() => copyText(label, value)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-slate-700 shrink-0"
                          aria-label={`Copy ${label}`}
                          data-testid={`bank-copy-${label.toLowerCase().replace(' ','-')}`}
                        >
                          {copied === label ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-700/60">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">
                      Amount
                    </p>
                    <p className="text-sm font-mono text-white">£{price} (or NGN equivalent)</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500">Loading bank details…</p>
              )}

              <p className="text-[11px] text-gray-400 leading-relaxed">
                After completing the transfer, tap the button below. An admin
                will verify your payment and message you with your private vault
                code.
              </p>

              {error && <p className="text-xs text-red-400">{error}</p>}

              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setStep('choose')}
                  className="flex-1 text-gray-300 border-slate-600"
                  data-testid="bank-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={submitBankTransfer}
                  disabled={busy}
                  className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bank-submit-btn"
                >
                  {busy ? 'Submitting…' : "I've Transferred · Notify Admin"}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'awaiting' && bankResult && (
          <>
            <Header title="Awaiting admin approval" />
            <div className="p-6 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-emerald-300" />
              </div>
              <div>
                <p className="text-sm text-gray-200">
                  Thanks! Your payment is being reviewed by an administrator.
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-wider text-gray-500">
                  Reference
                </p>
                <p className="font-mono text-base text-fuchsia-200" data-testid="bank-reference">
                  {bankResult.reference}
                </p>
              </div>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Once approved you'll receive your private vault code from an
                admin. You can close this window — we've already saved your
                request.
              </p>
              <Button
                onClick={onClose}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white"
                data-testid="awaiting-close-btn"
              >
                Got it
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyVaultAccessModal;
